import { vehicles } from '@trashlab/api/src/domains/asset/schemas/vehicles';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { organization_site } from '@trashlab/api/src/domains/dispatch/schemas/organization-site';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { sql } from 'drizzle-orm';
import {
  foreignKey,
  index,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
  unique,
  varchar,
} from 'drizzle-orm/pg-core';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';

/**
 * Enum defining the visibility scope of assets.
 * - HAULER: Asset visible in hauler/dispatch operations (default)
 * - SCALE: Asset visible in scale/transfer station operations
 */
export const asset_visibility_type = pgEnum('asset_visibility_type', [
  'HAULER',
  'SCALE',
]);

export const assets = pgTable(
  'assets',
  {
    ...defaultFields.fieldsWithTenantId(),
    // This is the primary identifier for the asset.
    userFacingId: varchar('userFacingId', { length: 255 }).notNull(),
    productId: uuid('productId').notNull(),
    organizationSiteId: uuid('organizationSiteId'),
    vehicleId: uuid('vehicleId'),
    setAtLocationTs: timestamp('setAtLocationTs', {
      withTimezone: true,
      mode: 'string',
    }),
    latitude: varchar('latitude', { length: 20 }),
    longitude: varchar('longitude', { length: 20 }),
    lastModifiedBy: uuid('lastModifiedBy'),
    customerSiteId: uuid('customerSiteId'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    notes: text('notes').default('').notNull(),
    /**
     * Visibility scope for this asset (HAULER and/or SCALE).
     * Determines which parts of the system can see and interact with this asset.
     * Default is HAULER only.
     */
    visibilityTypes: asset_visibility_type('visibilityTypes')
      .array()
      .default(sql`'{HAULER}'::asset_visibility_type[]`)
      .notNull(),
    /**
     * Optional reference to the customer who owns this asset.
     * Null means the asset is owned by the organization (global).
     */
    ownedByCustomerId: uuid('ownedByCustomerId'),
    searchables: text('searchables').generatedAlwaysAs(
      sql.raw(`"assets"."userFacingId"::text`),
    ),
    njdepContainerDecal: varchar('njdepContainerDecal', { length: 100 }),
  },
  (table) => {
    return {
      tenantId_id_unique: unique('assets_tenant_id_key').on(table.tenantId, table.id),
      capabilityPage: index('assets_capability_page_idx')
        .on(table.tenantId, sql`lower(${table.userFacingId})`, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      site_merge_page_idx: index('assets_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      createdAtTs_idx: index('assets_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('assets_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('assets_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('assets_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      tenant_updatedAtTs_id_active_idx: index(
        'assets_tenant_updatedAtTs_id_active_idx',
      )
        .on(table.tenantId, table.updatedAtTs.desc(), table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      trigram: index('asset_trigram').using(
        'gin',
        table.tenantId,
        table.searchables.op('gin_trgm_ops'),
      ),
      versionNumber_idx: index('assets_versionNumber_idx').on(
        table.versionNumber,
      ),

      customerSiteId_idx: index('assets_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      organizationSiteId_idx: index('assets_organizationSiteId_idx').on(
        table.organizationSiteId,
      ),
      productId_idx: index('assets_productId_idx').on(table.productId),
      vehicleId_idx: index('assets_vehicleId_idx').on(table.vehicleId),
      ownedByCustomerId_idx: index('assets_ownedByCustomerId_idx').on(
        table.ownedByCustomerId,
      ),
      mobileQrLookup_idx: index('assets_mobile_qr_lookup_idx')
        .on(table.tenantId, sql`lower(${table.userFacingId})`, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_userFacingId_id_idx: index('assets_tenant_userFacingId_id_idx').on(
        table.tenantId,
        table.userFacingId,
        table.id,
      ),
      tenant_display_page_active_idx: index(
        'assets_tenant_display_page_active_idx',
      )
        .on(
          table.tenantId,
          sql`(CASE WHEN "userFacingId" IS NULL OR btrim("userFacingId") = '' THEN 1 ELSE 0 END)`,
          sql`lower(left(btrim(COALESCE("userFacingId", '')), 48) COLLATE "C") COLLATE "C"`,
          sql`left(btrim(COALESCE("userFacingId", '')), 48) COLLATE "C"`,
          sql`md5(btrim(COALESCE("userFacingId", ''))) COLLATE "C"`,
          table.id,
        )
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_product_userFacingId_id_idx: index(
        'assets_tenant_product_userFacingId_id_idx',
      ).on(table.tenantId, table.productId, table.userFacingId, table.id),

      // Composite index for asset lookups by tenant and product
      tenant_product_idx: index('idx_assets_tenant_product').on(
        table.tenantId,
        table.productId,
      ),
      // Composite index for filtering by tenant, visibility, and customer ownership
      tenant_visibility_customer_idx: index(
        'idx_assets_tenant_visibility_customer',
      ).on(table.tenantId, table.visibilityTypes, table.ownedByCustomerId),
      // Composite index for hauler-specific asset lookups
      tenant_hauler_idx: index('idx_assets_tenant_hauler').on(
        table.tenantId,
        table.organizationSiteId,
        table.visibilityTypes,
      ),

      //foreign keys
      assets_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'assets_customerSiteId_fkey',
      }),
      assets_organizationSiteId_fkey: foreignKey({
        columns: [table.organizationSiteId],
        foreignColumns: [organization_site.id],
        name: 'assets_organizationSiteId_fkey',
      }),
      assets_productId_fkey: foreignKey({
        columns: [table.productId],
        foreignColumns: [product.id],
        name: 'assets_productId_fkey',
      }),
      assets_vehicleId_fkey: foreignKey({
        columns: [table.vehicleId],
        foreignColumns: [vehicles.id],
        name: 'assets_vehicleId_fkey',
      }),
      assets_ownedByCustomerId_fkey: foreignKey({
        columns: [table.ownedByCustomerId],
        foreignColumns: [customer.id],
        name: 'assets_ownedByCustomerId_fkey',
      }),
    };
  },
);

export const assets_history = pgTable('assets_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  userFacingId: varchar('userFacingId', { length: 255 }),
  productId: uuid('productId'),
  organizationSiteId: uuid('organizationSiteId'),
  vehicleId: uuid('vehicleId'),
  setAtLocationTs: timestamp('setAtLocationTs', {
    withTimezone: true,
    mode: 'string',
  }),
  latitude: varchar('latitude', { length: 20 }),
  longitude: varchar('longitude', { length: 20 }),
  lastModifiedBy: uuid('lastModifiedBy'),
  customerSiteId: uuid('customerSiteId'),
  createdBy: uuid('createdBy'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  notes: text('notes'),
  visibilityTypes: asset_visibility_type('visibilityTypes').array(),
  ownedByCustomerId: uuid('ownedByCustomerId'),
  searchables: text('searchables'),
  njdepContainerDecal: varchar('njdepContainerDecal', { length: 100 }),
});
